import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { sessionStorageKeys, tqQueryKeys } from "lib/constants";
import { EvendyApi } from "lib/utils";
import { ISODateString } from "lib/types";

export interface RequestItem {
  id: number;
  userEmail: string;
  title: string;
  type: "organizer_payout" | string;
  message: string;
  status: "pending" | "approved" | "rejected" | "completed" | string;
  userType: "organizer" | "vendor" | "admin" | string;
  resolvedAt: ISODateString | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

interface GetRequestsResponse {
  data: RequestItem[];
  page?: number;
  limit?: number;
  totalCount?: number;
}

interface UseGetRequestsOptions {
  page?: number;
  limit?: number;
  status?: string;
  userType?: string;
  search?: string;
}

type UseGetRequestsResult = UseQueryResult<GetRequestsResponse, Error>;

export function useGetRequests(options?: UseGetRequestsOptions): UseGetRequestsResult {
  const { page, limit, status, userType, search } = options || {};

  return useQuery({
    queryKey: [tqQueryKeys.requests.getRequests, page, limit, status, userType, search],
    queryFn: async () => {
      try {
        // Retrieve the Base64-encoded credentials from session storage
        const encodedCredentials = sessionStorage.getItem(sessionStorageKeys.accessToken);

        if (!encodedCredentials) {
          throw new Error("Authentication credentials not found.");
        }

        const params = new URLSearchParams();

        if (page) params.append("page", page.toString());
        if (limit) params.append("limit", limit.toString());
        if (status) params.append("status", status);
        if (userType) params.append("userType", userType);
        if (search) params.append("search", search);

        const res = await EvendyApi.get<GetRequestsResponse>(`requests?${params.toString()}`, {
          headers: {
            "x-api-key": encodedCredentials,
            "Content-Type": "application/json",
            accept: "application/json",
          },
        });

        return res.data;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || "Failed to fetch requests");
      }
    },
  });
}
